import { useCallback, useEffect, useRef, useState } from 'react';
import { imageDefinition, viewModelImageBasePath } from '../utils/imageDefinition';
import { createModelViewUrl, imageUrlBuilder } from '../utils/imageUrlProvider/imageUrlProvider';
import useFabricContext from '../providers/hooks/useFabricContext';
import { useEnvironmentContext } from '../providers/hooks/useEnvironmentContext';
import type { FabricImageData } from '../utils/interfaces';
import { useFeatureIsOn, useFeatureValue } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '../feature-flags/featureToggles';
import { ProductType } from '../utils/productDefinition';
import { getCropPositionByProductType } from '../utils/utils';
import { useEventCallback } from './useEventCallback';

const { ENABLE_MODEL_VIEW, MODEL_IMAGE_VERSION } = FEATURE_TOGGLES;

export const useModelViewImage = ({
  product,
  onLoad,
  onError,
}: {
  product: ProductType;
  onLoad: () => void;
  onError: () => void;
}) => {
  const { selectedFabric } = useFabricContext();
  const { cdnDomain } = useEnvironmentContext();
  const [isModelViewImageLoading, setIsModelViewImageLoading] = useState<boolean>(true);
  const [isModelViewThumbnailImageLoading, setIsModelViewThumbnailImageLoading] = useState<boolean>(true);
  const [modelViewImageData, setModelViewImageData] = useState<FabricImageData>({ src: '', srcset: '', sizes: '' });
  const [modelViewThumbnailImageData, setModelViewThumbnailImageData] = useState<FabricImageData>({ src: '', srcset: '', sizes: '' });
  const [isImageError, setIsImageError] = useState<boolean>(false);
  const modelViewFeatureEnabled = useFeatureIsOn(ENABLE_MODEL_VIEW);
  const fallbackModelImageVersion = useRef(crypto.randomUUID());
  const modelImageVersion = useFeatureValue(MODEL_IMAGE_VERSION, fallbackModelImageVersion.current);
  const activeModelViewImageUrlRef = useRef<string | undefined>(undefined);
  // A late load of one image can't clear the error of a fabric whose other image already failed.
  const hasActiveImageFailedRef = useRef(false);

  const clearImageError = () => {
    if (hasActiveImageFailedRef.current) return false;
    setIsImageError(false);
    return true;
  };

  // Only the full-size image reports the load, so the view switches when the photo can paint, not on a blank loader.
  const modelViewImageLoadedHandler = useEventCallback(() => {
    setIsModelViewImageLoading(false);
    if (clearImageError()) onLoad();
  });

  const modelViewThumbnailImageLoadedHandler = useEventCallback(() => {
    setIsModelViewThumbnailImageLoading(false);
    clearImageError();
  });

  const activateLoader = useCallback(() => {
    hasActiveImageFailedRef.current = false;
    setIsModelViewImageLoading(true);
    setIsModelViewThumbnailImageLoading(true);
  }, []);

  const imageErrorHandler = useEventCallback(() => {
    hasActiveImageFailedRef.current = true;
    setIsImageError(true);
    setIsModelViewImageLoading(false);
    setIsModelViewThumbnailImageLoading(false);
    onError();
  });

  useEffect(() => {
    if (!modelViewFeatureEnabled) return;

    if (selectedFabric) {
      const imageUrl = createModelViewUrl(`v${modelImageVersion}/${viewModelImageBasePath}`, selectedFabric.code, product);

      if (imageUrl === activeModelViewImageUrlRef.current) return;
      activeModelViewImageUrlRef.current = imageUrl;

      activateLoader();

      const crop = getCropPositionByProductType(product);

      setModelViewImageData(
        imageUrlBuilder({
          imageUrls: [imageUrl],
          imageDefinition: imageDefinition.layer,
          cdnDomain,
          crop,
        }),
      );
      setModelViewThumbnailImageData(
        imageUrlBuilder({
          imageUrls: [imageUrl],
          imageDefinition: imageDefinition.thumbnail,
          cdnDomain,
          crop,
        }),
      );
    }
  }, [selectedFabric, modelViewFeatureEnabled, modelImageVersion, product, cdnDomain, activateLoader]);

  useEffect(() => {
    if (!modelViewImageData.src || !modelViewThumbnailImageData.src) return;

    let cancelled = false;
    const thumbnailImage = new Image();

    thumbnailImage.onload = () => {
      if (!cancelled) modelViewThumbnailImageLoadedHandler();
    };
    thumbnailImage.onerror = () => {
      if (!cancelled) imageErrorHandler();
    };

    thumbnailImage.src = modelViewThumbnailImageData.src;

    // decode() resolves reliably for cached images (incl. Safari inside an
    // iframe), where the load event can be suppressed on a cache hit. It is only
    // used to confirm success early; a decode() rejection is intentionally
    // ignored because onerror is the authoritative failure signal (decode() can
    // reject spuriously on otherwise-valid images).
    thumbnailImage
      .decode()
      .then(() => {
        if (!cancelled) modelViewThumbnailImageLoadedHandler();
      })
      .catch(() => {
        // ignore: genuine load failures are surfaced via onerror
      });

    return () => {
      cancelled = true;
      thumbnailImage.onload = null;
      thumbnailImage.onerror = null;
    };
  }, [modelViewImageData.src, modelViewThumbnailImageData.src, modelViewThumbnailImageLoadedHandler, imageErrorHandler]);

  return {
    isImageError,
    isModelViewImageLoading,
    isModelViewThumbnailImageLoading,
    modelViewImageData,
    modelViewThumbnailImageData,
    modelViewImageLoadedHandler,
    modelViewThumbnailImageLoadedHandler,
    imageErrorHandler,
  };
};
